import { MimeType } from './MimeType';
import { MimeMapper } from './MimeMapper';
import { FileValidationReport, GLTFValidationReport } from './GltfFileResource';
import { validateGlb } from '.';
import { uploadMedia } from '../api/media';

export interface RemoteFileInfo {
    url: string;
    size: number;
    type: string;
    data: ArrayBuffer;
}

export type TAnyFileCollection = DataTransferItemList | FileList | null | undefined;

export class FileUtils {
    static getExtension(name: string): string {
        return this.getLastPathSegment(name, '.');
    }

    static getLastPathSegment(path: string, segment: string): string {
        const result = path.split(segment).pop();
        return result || '';
    }

    static getArrayBufferFromFile(file: File): Promise<ArrayBuffer> {
        return file.arrayBuffer();
    }

    static async getFileInfoFromUrl(url: string): Promise<RemoteFileInfo> {
        const req = await fetch(url);
        const type: MimeType = MimeMapper.fromType(req.headers.get('content-type'));
        const size: number = Number(req.headers.get('content-length') || 0);
        return {
            data: await req.arrayBuffer(),
            size,
            type,
            url
        };
    }

    static isGLTF(name: string, type: string, isURL: boolean = false): boolean {
        if (type === MimeType.GLTF) return true;
        const lastPathSegment: string = FileUtils.getLastPathSegment(name, isURL ? '/' : '.');

        if (lastPathSegment === 'gltf' || lastPathSegment === 'glb') return true;

        return false;
    }
}

const getFileObject = (file: File | DataTransferItem): File | null => {
    if (file instanceof DataTransferItem) {
        return file.getAsFile();
    } else {
        return file;
    }
};

export const validateFiles = async (files: TAnyFileCollection): Promise<PromiseSettledResult<FileValidationReport>[]> => {
    const promiseArray: Promise<FileValidationReport>[] = [];
    if (files) {
        for (let index = 0; index < files.length; index++) {
            const orginalFile = files[index];
            const file = getFileObject(orginalFile);
            if (file && file instanceof File) {
                const report = await validateGlb(file);
                if (!report) {
                    promiseArray.push(Promise.reject({ valid: false, size: file.size, name: file.name, time: file.lastModified as unknown as Date, report }));
                } else {
                    promiseArray.push(Promise.resolve({ valid: true, size: file.size, name: file.name, time: file.lastModified as unknown as Date, report }));
                }
            }
        }
    }
    const validatedArrayPromise = Promise.allSettled(promiseArray);
    return validatedArrayPromise;
};

export const uploadFiles = async (files: TAnyFileCollection, requestId: string, validatedFilesMapping: PromiseSettledResult<FileValidationReport>[]) => {
    if (files && requestId) {
        for (let index = 0; index < files.length; index++) {
            const orginalFile = files[index];
            const file = getFileObject(orginalFile);
            const productId = file?.name.replace('.glb', '');
            if (file && productId && (validatedFilesMapping[index] as PromiseFulfilledResult<FileValidationReport>).value.report?.valid === true) {
                await uploadMedia(requestId, productId, file);
            }
        }
    }
};

export const getSizeInMb = (sizeInBytes: number): string => {
    return (sizeInBytes / (1024 * 1024)).toFixed(2) + ' mb';
};

export const downloadNewFile = (data: string, fileName: string, type: string) => {
    let file = new File([data], fileName, { type });
    let exportUrl = URL.createObjectURL(file);
    window.location.assign(exportUrl);
    URL.revokeObjectURL(exportUrl);
};

export const downloadFileAsBlob = (data, fileName, type) => {
    // Encode data as UTF-8
    const encodedData = new TextEncoder().encode(data);

    // Create a Blob object from the encoded data
    const blob = new Blob([encodedData], { type });

    // Create a URL for the Blob object
    const url = URL.createObjectURL(blob);

    // Create a link element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    // Trigger the download
    document.body.appendChild(link);
    link.click();

    // Clean up
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
};
